<script setup lang="ts">
/**
 * SkipButtonsMockup — 快进快退浮动按钮 mockup
 * v8 真实能力（progress-bar-controller.js）：视频播放时显示 ±N 快进/快退浮动条，
 * 支持自定义步长（10/30/60/300/600/1800 秒，默认 30s），自由控制观看进度。
 * 播放中显示，暂停时按站点配置可隐藏。
 */
import { ref, computed } from 'vue'

const seconds = ref(90)
const totalSeconds = 90 * 60 + 45 // 90:45
const skip = ref(30)
const isPlaying = ref(true)

const STEP_OPTIONS = [10, 30, 60, 300]

const sliderPercent = computed(() => (seconds.value / totalSeconds) * 100)

function formatTime(s: number): string {
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

function seek(delta: number) {
  seconds.value = Math.max(0, Math.min(totalSeconds, seconds.value + delta))
}

function togglePlay() {
  isPlaying.value = !isPlaying.value
}
</script>

<template>
  <div class="sb-mock">
    <div class="sb-mock__header">
      <span class="sb-mock__label">快进快退</span>
      <button type="button" class="sb-mock__play yfb-press" @click="togglePlay">
        {{ isPlaying ? '❚❚' : '▶' }}
      </button>
    </div>
    <div class="sb-mock__player">
      <div class="sb-mock__video">
        <!-- 视频画面：进度条已被防护隐藏，只剩时间 -->
        <div class="sb-mock__frame">
          <div class="sb-mock__time">{{ formatTime(seconds) }} / {{ formatTime(totalSeconds) }}</div>

          <!-- 浮动 ±N 按钮条（常显，不随播放状态隐藏） -->
          <div class="sb-mock__bar">
            <button type="button" class="sb-mock__seek yfb-press" @click="seek(-skip)">
              <span class="sb-mock__seek-icon" aria-hidden="true">↺</span>
              <span class="sb-mock__seek-num">{{ skip }}s</span>
            </button>
            <button type="button" class="sb-mock__seek yfb-press" @click="seek(skip)">
              <span class="sb-mock__seek-num">{{ skip }}s</span>
              <span class="sb-mock__seek-icon" aria-hidden="true">↻</span>
            </button>
          </div>
        </div>

        <!-- 步长设置 -->
        <div class="sb-mock__controls">
          <span class="sb-mock__ctrl-label">步长</span>
          <div class="sb-mock__steps">
            <button
              v-for="opt in STEP_OPTIONS"
              :key="opt"
              type="button"
              class="sb-mock__step yfb-press"
              :class="{ 'sb-mock__step--active': skip === opt }"
              @click="skip = opt"
            >
              {{ opt }}s
            </button>
          </div>
        </div>
        <div class="sb-mock__hint">点按按钮 ±{{ skip }}s 跳转 · 步长可自定义</div>
      </div>
    </div>
    <div class="sb-mock__footer">按自己的节奏推进 · 灵活控制进度</div>
  </div>
</template>

<style scoped>
.sb-mock {
  border: var(--yfb-border-2) solid var(--yfb-rule);
  background-color: var(--yfb-surface);
  box-shadow: var(--yfb-shadow-2);
  border-radius: var(--yfb-radius-lg);
  overflow: hidden;
}

.sb-mock__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--yfb-sp-3) var(--yfb-sp-4);
  background-color: var(--yfb-surface-2);
  color: var(--yfb-ink);
  border-bottom: 1px solid var(--yfb-rule-soft);
}

.sb-mock__label {
  font-family: var(--yfb-font-heading);
  font-size: 10px;
  font-weight: 700;
  color: var(--yfb-accent);
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-eyebrow);
}

.sb-mock__play {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  border: none;
  border-radius: var(--yfb-radius-sm);
  font-size: 12px;
  cursor: pointer;
}

.sb-mock__player {
  padding: var(--yfb-sp-4);
}

.sb-mock__video {
  background-color: var(--yfb-bg);
  padding: var(--yfb-sp-4);
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-3);
  border-radius: var(--yfb-radius-md);
  box-shadow: inset 0 0 0 1px var(--yfb-rule-soft);
}

.sb-mock__frame {
  position: relative;
  aspect-ratio: 16 / 9;
  background-color: var(--yfb-panel-bg);
  border: var(--yfb-border-2) solid var(--yfb-rule-soft);
  border-radius: var(--yfb-radius-md);
  overflow: hidden;
}

.sb-mock__frame::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    repeating-linear-gradient(0deg, transparent 0, transparent 14px, var(--yfb-grid-line) 14px, var(--yfb-grid-line) 15px),
    repeating-linear-gradient(90deg, transparent 0, transparent 14px, var(--yfb-grid-line) 14px, var(--yfb-grid-line) 15px);
}

.sb-mock__time {
  position: absolute;
  top: var(--yfb-sp-2);
  left: var(--yfb-sp-2);
  z-index: 1;
  font-family: var(--yfb-mono);
  font-size: 10px;
  font-weight: 700;
  color: var(--yfb-accent);
  background: rgba(11, 11, 16, 0.7);
  padding: 2px 6px;
  border-radius: var(--yfb-radius-sm);
  letter-spacing: var(--yfb-ls-mono);
}

/* 浮动 ±N 按钮条 */
.sb-mock__bar {
  position: absolute;
  left: 50%;
  bottom: 12px;
  z-index: 2;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  background: rgba(11, 11, 16, 0.78);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-md);
  box-shadow: var(--yfb-shadow-2);
}

.sb-mock__seek {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  min-width: 48px;
  height: 24px;
  padding: 0 6px;
  background: transparent;
  color: var(--yfb-ink);
  border: none;
  border-radius: var(--yfb-radius-sm);
  cursor: pointer;
  font-family: var(--yfb-mono);
  font-size: 10px;
  font-weight: 700;
}

.sb-mock__seek:hover {
  background: var(--yfb-accent);
  color: var(--yfb-on-accent);
}

.sb-mock__seek-icon {
  font-size: 12px;
  line-height: 1;
}

.sb-mock__seek-num {
  letter-spacing: var(--yfb-ls-mono);
}

/* 控制条 */
.sb-mock__controls {
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-3);
}

.sb-mock__ctrl-label {
  font-family: var(--yfb-mono);
  font-size: 9px;
  font-weight: 700;
  color: var(--yfb-text-secondary);
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
  flex-shrink: 0;
}

.sb-mock__steps {
  display: flex;
  gap: 6px;
}

.sb-mock__step {
  padding: 4px 10px;
  border: 1.5px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
  background: var(--yfb-surface-2);
  color: var(--yfb-text-secondary);
  font-family: var(--yfb-mono);
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: color var(--yfb-anim-fast) ease-out, border-color var(--yfb-anim-fast) ease-out, background-color var(--yfb-anim-fast) ease-out;
}

.sb-mock__step:hover {
  color: var(--yfb-accent);
  border-color: var(--yfb-accent);
}

.sb-mock__step--active {
  background: var(--yfb-accent);
  color: var(--yfb-on-accent);
  border-color: var(--yfb-accent);
}

.sb-mock__hint {
  font-family: var(--yfb-mono);
  font-size: 11px;
  font-weight: 700;
  color: var(--yfb-text-secondary);
  text-align: center;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
}

.sb-mock__footer {
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-2);
  padding: var(--yfb-sp-2) var(--yfb-sp-4);
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  font-family: var(--yfb-mono);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
  border-top: 1px solid var(--yfb-rule);
  justify-content: center;
}
</style>
